import { useState, type ReactNode } from "react";
import {
  Alert,
  ApiError,
  Badge,
  Button,
  Modal,
  apiFetch,
  closureTimeLeft,
  fmtLongDate,
  type PortalAccountClosure,
} from "@dearbill/shared";
import { HomeCard } from "../home/HomeCard";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";
const ENDPOINT = "/api/portal/account-closure";
const NOTE = "text-body-sm text-slate-teal";
const FAILURE = "We couldn't update your account. Please try again.";

const IDLE_BODY =
  "Closing your account permanently deletes your enrollment forms, medication list, Medicare data and call records from DearBill and disconnects your Medicare account. Deletion happens 30 days after your request, and you can cancel any time before then from this page.";

interface ClosureAction {
  method: "POST" | "DELETE";
  /** The card's button, which opens the confirm dialog. */
  button: string;
  title: string;
  body: string;
  confirm: string;
}

const REQUEST: ClosureAction = {
  method: "POST",
  button: "Request Account Closure",
  title: "Close your DearBill account?",
  body: "All of your data will be permanently deleted from DearBill in 30 days, including your enrollment forms, medication list, Medicare data and call records. Your Medicare account will be disconnected. You can cancel this request any time before then from Settings.",
  confirm: "Request closure",
};

const CANCEL: ClosureAction = {
  method: "DELETE",
  button: "Cancel Account Closure",
  title: "Keep your DearBill account?",
  body: "Your closure request will be withdrawn and nothing will be deleted. You can request closure again at any time.",
  confirm: "Keep my account",
};

interface CloseAccountCardProps {
  /** The dashboard's pending request; null when none is pending. */
  closure: PortalAccountClosure | null;
  /** A caregiver session: the card shows the copy and status but no button. */
  readOnly: boolean;
  /** Portal session bearer for the request and the cancel. */
  session: string;
  /** Refetches the dashboard, whose `accountClosure` then drives the card. */
  reload: () => void;
  /** Called when a request is rejected with 401. */
  onSignOut: () => void;
}

/**
 * The Close DearBill Account card. With nothing pending it explains the
 * closure and offers "Request Account Closure"; pending, it carries the
 * "Closure requested" badge, the days left until deletion and "Cancel Account
 * Closure". Either button opens a confirm dialog whose confirm sends `POST`
 * or `DELETE /api/portal/account-closure` and then `reload`s. A failure
 * closes the dialog and shows an error under the button; a 409 means the
 * stamp changed under the screen, so it also reloads. A 401 signs out.
 */
export function CloseAccountCard({
  closure,
  readOnly,
  session,
  reload,
  onSignOut,
}: CloseAccountCardProps): ReactNode {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const action = closure === null ? REQUEST : CANCEL;

  async function submit(): Promise<void> {
    setBusy(true);
    setFailed(false);
    let refetch = true;
    try {
      await apiFetch(ENDPOINT, { method: action.method }, { baseUrl: API_BASE, token: session });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSignOut();
        return;
      }
      setFailed(true);
      refetch = err instanceof ApiError && err.status === 409;
    } finally {
      setBusy(false);
      setConfirming(false);
    }
    if (refetch) reload();
  }

  return (
    <HomeCard
      title="Close DearBill Account"
      titleSize="lg"
      titleBadge={closure !== null && <Badge tone="pending">Closure requested</Badge>}
    >
      {/* A polite live region, so the switch between the idle copy and the
          pending status after a request or cancel is announced. */}
      <div aria-live="polite" className="flex flex-col gap-1">
        {closure === null ? (
          <p className={NOTE}>{IDLE_BODY}</p>
        ) : (
          <>
            <p className="text-body-sm text-midnight font-bold">
              Your account will be deleted {closureTimeLeft(closure.scheduledFor, Date.now())}.
            </p>
            <p className={NOTE}>
              Requested on {fmtLongDate(closure.requestedAt)}. You can cancel until your data is
              deleted.
            </p>
          </>
        )}
      </div>
      {/* One button element for both states, so focus stays on it when the
          refetch swaps Request for Cancel and back. */}
      {!readOnly && (
        <Button
          variant="outlined"
          size="md"
          className="self-start"
          onClick={() => setConfirming(true)}
        >
          {action.button}
        </Button>
      )}
      {failed && <Alert tone="error" title={FAILURE} onDismiss={() => setFailed(false)} />}
      <Modal
        open={confirming}
        title={action.title}
        onClose={() => {
          if (!busy) setConfirming(false);
        }}
      >
        <p className={NOTE}>{action.body}</p>
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="ghost" disabled={busy} onClick={() => setConfirming(false)}>
            Go back
          </Button>
          <Button variant="primary" disabled={busy} onClick={() => void submit()}>
            {action.confirm}
          </Button>
        </div>
      </Modal>
    </HomeCard>
  );
}
